// @core/stores/payment/PaymentWebhookStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  HandleProviderWebhookRequest,
  HandleProviderWebhookResponse,
  ProviderEvent,
  RetryProviderEventRequest,
  RetryProviderEventResponse,
} from '@core/types/payment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PaymentWebhookStore {
  // List state
  items: ProviderEvent[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: ProviderEvent | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, ProviderEvent>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): ProviderEvent | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: ProviderEvent | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Handle provider webhook
   */
  async HandleProviderWebhook(providerCode: string, data: HandleProviderWebhookRequest): Promise<HandleProviderWebhookResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.HandleProviderWebhook(providerCode, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List provider events
   */
  async ListProviderEvents(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListProviderEvents(params);

      runInAction(() => {
        this.items = response.provider_events?.events ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry processing of a provider event
   */
  async RetryProviderEvent(id: string, data: RetryProviderEventRequest): Promise<RetryProviderEventResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await paymentApi.RetryProviderEvent(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const paymentWebhookStore = new PaymentWebhookStore();
